import {
  Box,
  Button,
  Card,
  Center,
  FancyCard,
  Icon,
  Tag,
  Title,
} from "components";
import { Answer } from "../components";
import { useGetQuery, usePostQuery } from "hooks";
import { useSocket } from "providers";
import { apiFetch } from "actions/apiFetch";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { PendingTriviaGame, PlayerList, PlayerStatus, CopyText } from "./components";

export const QuestionRouter = ({ item }) => {
  switch (item.details.type) {
    case "multiple":
      return <Multiple item={item} />;
    case "boolean":
      return <TrueFalse item={item} />;
    default:
      return <>not yet</>;
  }
};

// Shuffle array with a seed for consistent ordering
const seededShuffle = (array, seed) => {
  const shuffled = [...array]
  let currentIndex = shuffled.length
  
  const seededRandom = () => {
    seed = (seed * 9301 + 49297) % 233280
    return seed / 233280
  }
  
  while (currentIndex > 0) {
    const randomIndex = Math.floor(seededRandom() * currentIndex)
    currentIndex--
    ;[shuffled[currentIndex], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[currentIndex]]
  }
  
  return shuffled
}

export const Multiple = ({ item }) => {
  const [answers, setAnswers] = useState([]);
  useEffect(() => {
    if (!item?.details) return;
    // Create a copy to avoid mutating the original
    const allAnswers = [...(item.details.incorrect_answers || [])];
    if (!allAnswers.includes(item.details.correct_answer)) {
      allAnswers.push(item.details.correct_answer);
    }
    // Use item ID as seed for consistent shuffle
    const seed = item.id ? item.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0;
    setAnswers(seededShuffle(allAnswers, seed));
  }, [item?.id]); // Track by ID, not the whole item object
  return (
    <>
      <Box padding="0.5rem 0">
        {answers.map((a) => (
          <Answer key={a}>
            <span dangerouslySetInnerHTML={{ __html: a }}></span>
            <>
              {a === item.details.correct_answer ? <Icon icon="check" /> : null}
            </>
          </Answer>
        ))}
      </Box>
    </>
  );
};

export const TrueFalse = ({ item }) => {
  return (
    <>
      <Box padding="0.5rem 0">{item.details.correct_answer}</Box>
    </>
  );
};

export const ActiveTriviaGame = ({ queue, reFetch, endGame }) => {
  const { post: putItem } = usePostQuery({
    url: "/queue_items/",
    method: "put",
  });
  const { post: advanceQuestion } = usePostQuery({
    url: "/queue_items/advance",
    method: "post",
  });
  
  // Get all items from the queue prop
  const items = queue?.QueueItems || [];
  
  // Find current active question from the queue items
  const currentItem = items.find((i) => i.state === "active");
  
  // Find next pending question
  const nextPending = items.find((i) => i.state === "pending");

  const doNext = async () => {
    if (!currentItem) return;
    
    // Check if there's a next pending question
    if (!nextPending?.id) {
      // No more questions - mark current as completed and end game
      await putItem({ state: "completed" }, `/queue_items/${currentItem.id}`);
      return endGame();
    }
    
    // Advance to next question atomically - single API call, single socket event
    await advanceQuestion({ currentId: currentItem.id, nextId: nextPending.id });
  };

  if (!currentItem) {
    return (
      <Center>
        <Card>Loading...</Card>
      </Center>
    );
  }

  // Get players and their answer status for current question
  const players = queue?.players || [];
  const questionDetails = currentItem?.details || {};
  const correctAnswer = questionDetails.correct_answer;

  return (
    <Box padding="0.5rem">
      <FancyCard className="accent" padding="1rem" margin="0.5rem">
        <Title
          level="3"
          dangerouslySetInnerHTML={{ __html: currentItem.details.question }}
        />

        <QuestionRouter item={currentItem} />
      </FancyCard>
      
      <Card padding="1rem" margin="0.5rem 0">
        <Title level="4">Players ({players.length})</Title>
        <PlayerList>
          {players.map((player) => {
            const playerAnswer = questionDetails[player.email];
            const hasAnswered = playerAnswer !== undefined;
            const isCorrect = hasAnswered && playerAnswer === correctAnswer;
            
            return (
              <PlayerStatus key={player.id} className={hasAnswered ? (isCorrect ? 'correct' : 'incorrect') : 'waiting'}>
                <span>{player.email}</span>
                <span>
                  {hasAnswered ? (
                    <Icon icon={isCorrect ? 'check' : 'x'} />
                  ) : (
                    '...'
                  )}
                </span>
              </PlayerStatus>
            );
          })}
        </PlayerList>
      </Card>

      <Button onPress={doNext}>Next</Button>
      <Card>
        <CopyText>{`${document.location.origin}/guest/trivia/${queue.slug}`}</CopyText>
      </Card>
    </Box>
  );
};

export const CompletedTriviaGame = ({ queue }) => {
  return (
    <Center>
      <Card>Completed</Card>
    </Center>
  );
};

export const HostTriviaGame = ({}) => {
  const { id } = useParams();
  const socket = useSocket();
  const { error: postError, post } = usePostQuery({ url: `/queues/${id}` });

  const { data, isFetching, reFetch } = useGetQuery({
    url: `/host/queue/${id}`,
  });
  
  // Local state to hold queue data, updated by socket events
  const [queue, setQueue] = useState(null);
  
  // Initialize from fetched data
  useEffect(() => {
    if (data?.queue) {
      setQueue(data.queue);
    }
  }, [data?.queue]);

  const startGame = async () => {
    // Create trivia questions if none exist
    if (!queue?.QueueItems?.length) {
      await apiFetch({ method: 'PUT', path: '/trivia', payload: { id: queue?.slug, count: 10 } });
    }
    // Set queue state to active - first question is already set to active by the API
    await post({ state: "active" });
    // The socket event will update the queue state
    await reFetch(true);
  };

  // Use the queue's slug for socket channel (API emits using slug, not UUID)
  useEffect(() => {
    if (!queue?.slug) return;
    const channel = `/trivia/${queue.slug}`;
    const handleSocketEvent = (ev) => {
      // Use socket data directly instead of refetching
      if (ev.type === "UPDATE_QUEUE_SUCCESS" && ev.data) {
        setQueue(ev.data);
      }
    };
    socket.on(channel, handleSocketEvent);
    return () => socket.off(channel, handleSocketEvent);
  }, [socket, queue?.slug]);

  const endGame = () => {
    post({ state: "completed" }).then(
      (d = console.log("COMPLETED", d))
    );
  };

  if (isFetching && !queue) return <Card>Loading...</Card>;
  if (!queue) return <Card>Loading...</Card>;

  const queueState = queue.state;
  const hasQueueItems = queue.QueueItems?.length > 0;

  if (queueState === "active" && hasQueueItems) {
    return (
      <ActiveTriviaGame
        queue={queue}
        reFetch={() => setQueue({ ...queue })}
        endGame={endGame}
      />
    );
  }

  if (queueState === "active" && !hasQueueItems) {
    return <Card>Loading questions...</Card>;
  }

  if (queueState === "completed") {
    return <CompletedTriviaGame queue={queue} />;
  }

  // pending or unknown state
  return (
    <PendingTriviaGame
      queue={queue}
      reFetch={() => setQueue({ ...queue })}
      startGame={startGame}
    />
  );
};
